/**
 * FondCommune — le fond d'écran choisi par la mairie (mig 294).
 *
 * GÉNÉRALISÉ le 20/08 (décision Henrico après l'essai technique) : monté UNE
 * SEULE fois dans App.tsx, sous les providers — donc présent sur TOUTES les
 * pages connectées, briques comprises, sans une ligne par page. Hors
 * connexion le composant ne fait rien (pages publiques inchangées). Ne JAMAIS
 * le monter aussi dans une page : deux couches = deux voiles superposés.
 *
 * · L'image passe SOUS un voile `bg-bg/85` : blanc en mode clair, sombre en
 *   mode nuit — le token du thème fait la bascule, aucun code conditionnel.
 * · Couche fixe en -z-10 : les cartes gardent leurs propres fonds, seule la
 *   « toile » de la page change.
 * · Cache module : une seule requête `/api/me/fond-ecran` par chargement de
 *   l'application, même si dix pages montent le composant — pas une rafale
 *   d'appels à chaque navigation.
 */
import { useEffect, useState } from 'react';
import { api, type FondEcran } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';

let cache: Promise<FondEcran> | null = null;
// Les couches montées s'abonnent : quand la mairie change un réglage, elles
// se mettent à jour SANS F5 — c'était le trou de la v4 (constat Henrico,
// 20/08 après-midi : « ça fonctionne mais il faut un refresh à chaque fois »).
const abonnes = new Set<(f: FondEcran) => void>();

function fondPartage(): Promise<FondEcran> {
  cache ??= api.admin.tenant.fondEcran().catch((e): never => {
    cache = null; // un échec ne doit pas empoisonner toute la session
    throw e;
  });
  return cache;
}

/**
 * À appeler après un changement dans Administration → Mairie. Avec la valeur
 * fraîche renvoyée par l'API (choix, upload, voile) : zéro requête en plus,
 * toutes les couches montées basculent immédiatement. Sans valeur : on
 * recharge puis on prévient.
 */
export function invaliderFondCommune(frais?: FondEcran): void {
  if (frais) {
    cache = Promise.resolve(frais);
    abonnes.forEach((cb) => cb(frais));
    return;
  }
  cache = null;
  fondPartage().then((f) => abonnes.forEach((cb) => cb(f))).catch(() => {});
}

export function FondCommune() {
  const { user } = useAuth();
  const [fond, setFond] = useState<FondEcran | null>(null);
  useEffect(() => {
    if (!user) { setFond(null); return; }
    let actif = true;
    const maj = (f: FondEcran) => { if (actif) setFond(f); };
    abonnes.add(maj);
    fondPartage().then(maj).catch(() => {});
    return () => { actif = false; abonnes.delete(maj); };
  }, [user]);
  if (!fond?.url) return null;
  return (
    <div aria-hidden className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
      <img src={fond.url} alt="" className="h-full w-full object-cover" />
      {/* Voile réglé par la mairie (mig 296) — toujours le token du thème :
          blanc en clair, sombre en nuit, seule l'INTENSITÉ change. */}
      <div className="absolute inset-0 bg-bg" style={{ opacity: fond.voile / 100 }} />
    </div>
  );
}
